import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { endpointsCf } from "../config/componentsConfig";
import { loginRequest } from "../config/authConfig";
import { useMsal } from "@azure/msal-react";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { keysToCamelCase } from "../utils/caseConversion";
import { taskFromLegalJobRow } from "../utils/legalJobFromServer";
import UploadNotification from "../components/Page/Menu/FileSelector/FileUpload/UploadNotification";
import { Task, TaskStatus, TaskType } from "../types/taskTypes";
import {
  mergeLegalProgressIntoTask,
  parseLegalSearchProgressPayload,
} from "../utils/legalSearchTaskProgress";
import {
  LEGAL_JOBS_SYNC_NOW_EVENT,
  LEGAL_TASK_ADDED_EVENT,
  LEGAL_TASK_PROGRESS_EVENT,
  clearPendingLegalTaskSession,
} from "../utils/legalSearchTaskFlow";

/** Synchronisation des jobs exhaustive (index ES, GET /legal/legal_jobs/{user}) — tous les modes RAG. */
const LEGAL_JOBS_SYNC_MS = 60_000;
/** Panneau latéral « My Tasks » (selectedMenu === 5) : rafraîchissement plus fréquent. */
const LEGAL_JOBS_SYNC_TASKS_PANEL_MS = 10_000;

const RAG_PAGES_WITH_EXHAUSTIVE_JOBS = new Set([
  "legal",
  "market",
  "mydeal",
  "oreima",
]);

export type { Task };
export { TaskStatus, TaskType };

interface TasksContextProps {
  tasks: Array<Task>;
  setTasks: (tasks: Array<Task>) => void;
  addTask: (task: Task) => void;
  removeTask: (task: Task) => void;
  selectFilebyName: (name: string) => void;
  updateTask: (task: Task) => void;
}

export const TasksContext = createContext<TasksContextProps | undefined>(undefined);

export const TasksProvider: React.FC<{
  children: React.ReactNode;
  page: string;
  fileSelectorRef: any;
  selectedMode: string;
  /** 5 = panneau My Tasks — sync plus agressive (10 s). */
  selectedMenu?: number;
}> = ({
  children,
  page,
  fileSelectorRef,
  selectedMode,
  selectedMenu = 0,
}) => {
  const mode = selectedMode=="MYGEORGES" ? "mygeorges" : page;

  const notificationRef = useRef<Notification>(null);

  const selectFilebyName = (name: string) => {
    fileSelectorRef.current?.selectFilebyName(name);
  }

  const getCleanTasksFromLocalStorage = () => {
    const storedValue = localStorage.getItem("tasks");
    if (storedValue) {
      var tasks = JSON.parse(storedValue);
      return tasks;
    }
    return [];
  }

  const [tasks, setTasks] = useState<Array<Task>>(
    getCleanTasksFromLocalStorage()
  );

  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const { execute } = useFetchWithMsal();
  const userId = activeAccount?.username;
  const [pollingTasks, setPollingTasks] = useState<Array<Task>>([]); //Store which tasks are already being polled

  /** GET/DELETE JSON avec jeton MSAL (sans spinner global). */
  const fetchJsonAuthorized = useCallback(
    async (url: string, method: "GET" | "DELETE" = "GET") => {
      const account = instance.getActiveAccount();
      if (!account) return null;
      const { accessToken } = await instance.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account,
      });
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${method} ${res.status}: ${text.slice(0, 300)}`);
      }
      if (method === "DELETE" || res.status === 204) return {};
      const json = await res.json();
      return keysToCamelCase(json) as Record<string, unknown>;
    },
    [instance]
  );

  useEffect(() => {
    localStorage.setItem("tasks", JSON.stringify(tasks));
  }, [tasks]);

  useEffect(() => {
    tasks.forEach(task => {
      if(datesDifferenceInMilliseconds(new Date(), new Date(task.createdAt)) > 1000 * 60 * 60 * 24 * 5) {
        removeTask(task);
      }
    });
  }, []);

  const addTask = useCallback((task: Task) => {
    const enriched: Task = {
      ...task,
      mode,
      page,
    };
    setTasks((prevTasks) => {
      if (prevTasks.some((t) => t.id === enriched.id)) return prevTasks;
      const next = [...prevTasks, enriched];
      // Tâches les plus récentes en premier (date de création).
      return next.sort((a, b) => b.createdAt - a.createdAt);
    });
  }, [mode, page]);

  const addTaskRef = useRef(addTask);
  addTaskRef.current = addTask;

  useLayoutEffect(() => {
    const onAddLegalTask = (e: Event) => {
      const detail = (e as CustomEvent<Task>).detail;
      if (detail?.id) {
        addTaskRef.current(detail);
      }
    };
    const onLegalProgress = (e: Event) => {
      const raw = (e as CustomEvent<Record<string, unknown>>).detail;
      const p = parseLegalSearchProgressPayload(raw);
      if (!p) return;
      const now = Date.now();
      setTasks((prev) =>
        prev.map((t) => {
          if (t.id !== p.taskId) return t;
          if (p.status === "completed") {
            clearPendingLegalTaskSession();
          }
          return mergeLegalProgressIntoTask(t, p, now);
        })
      );
    };
    window.addEventListener(LEGAL_TASK_ADDED_EVENT, onAddLegalTask);
    window.addEventListener(LEGAL_TASK_PROGRESS_EVENT, onLegalProgress);
    return () => {
      window.removeEventListener(LEGAL_TASK_ADDED_EVENT, onAddLegalTask);
      window.removeEventListener(LEGAL_TASK_PROGRESS_EVENT, onLegalProgress);
    };
  }, []);

  /**
   * Jobs exhaustive (ES) : même liste pour legal / market / mydeal / oreima.
   * Fusionne avec les tâches locales encore absentes du serveur.
   * Panneau My Tasks (menu 5) : 10 s ; sinon 60 s.
   */
  useEffect(() => {
    if (!userId || !RAG_PAGES_WITH_EXHAUSTIVE_JOBS.has(page)) return;

    const sync = async () => {
      try {
        const data = await fetchJsonAuthorized(
          endpointsCf.legalJobs(userId),
          "GET"
        );
        const jobs = (data?.jobs as unknown[]) ?? [];
        setTasks((prev) => {
          const rows = jobs.map(
            (j) => keysToCamelCase(j as object) as Record<string, unknown>
          );
          const prevById = new Map(prev.map((t) => [t.id, t] as const));
          const apiIds = new Set(
            rows.map((r) => String(r.taskId ?? r.task_id ?? ""))
          );
          const isRagLegalSearch = (t: Task) =>
            t.type === TaskType.LEGAL_SEARCH &&
            RAG_PAGES_WITH_EXHAUSTIVE_JOBS.has(String(t.page ?? t.mode ?? ""));
          const nonRagJob = prev.filter((t) => !isRagLegalSearch(t));
          const localOnly = prev.filter((t) => {
            if (!(isRagLegalSearch(t) && !apiIds.has(t.id))) return false;
            // Évite les "fantômes" persistés localement : on ne garde que
            // les tâches locales récentes, le temps que le backend les expose.
            const ageMs = Date.now() - Number(t.createdAt || 0);
            return ageMs < 2 * 60 * 1000;
          });
          const fromApi = rows.map((r) => {
            const next = taskFromLegalJobRow(
              r,
              String(r.page ?? page),
              String(r.page ?? page)
            );
            const prevTask = prevById.get(next.id);
            // Evite l'effet "double-clic Annuler":
            // si l'utilisateur vient d'annuler localement, ne pas réafficher
            // un état processing/summarizing transitoire provenant du poll serveur.
            if (
              prevTask?.status === TaskStatus.CANCELLED &&
              (next.status === TaskStatus.PROCESSING ||
                next.status === TaskStatus.SUMMARIZING)
            ) {
              return {
                ...prevTask,
                // On garde la progression la plus fraîche.
                file: {
                  ...prevTask.file,
                  nbPages: Math.max(prevTask.file.nbPages, next.file.nbPages),
                  nbProcessed: Math.max(
                    prevTask.file.nbProcessed,
                    next.file.nbProcessed
                  ),
                },
              };
            }
            // Annulation locale : ne pas écraser par un « error » transitoire du poll avant reco ES.
            if (
              prevTask?.status === TaskStatus.CANCELLED &&
              next.status === TaskStatus.ERROR
            ) {
              return prevTask;
            }
            return {
              ...next,
              legalWipJobFormula:
                next.legalWipJobFormula ?? prevTask?.legalWipJobFormula,
              legalWipPhase:
                next.legalWipPhase ?? prevTask?.legalWipPhase,
              legalProgressDetail:
                next.legalProgressDetail ?? prevTask?.legalProgressDetail,
            };
          });
          const merged = [...nonRagJob, ...fromApi, ...localOnly];
          // Tri global par date de création (tâches les plus récentes d'abord).
          return merged.sort((a, b) => b.createdAt - a.createdAt);
        });
      } catch (e) {
        console.error("[exhaustive jobs sync]", e);
      }
    };

    void sync();
    const onSyncNow = () => void sync();
    window.addEventListener(LEGAL_JOBS_SYNC_NOW_EVENT, onSyncNow);
    const intervalMs =
      selectedMenu === 5 ? LEGAL_JOBS_SYNC_TASKS_PANEL_MS : LEGAL_JOBS_SYNC_MS;
    const id = window.setInterval(() => void sync(), intervalMs);
    return () => {
      window.removeEventListener(LEGAL_JOBS_SYNC_NOW_EVENT, onSyncNow);
      window.clearInterval(id);
    };
  }, [userId, page, selectedMenu, fetchJsonAuthorized]);

  const updateTask = (task: Task) => {
    setTasks(prevTasks => prevTasks.map(t => t.id === task.id ? task : t));
  };

  const removeTask = useCallback(
    (task: Task) => {
      if (
        task.type === TaskType.LEGAL_SEARCH &&
        RAG_PAGES_WITH_EXHAUSTIVE_JOBS.has(
          String(task.page ?? task.mode ?? "")
        ) &&
        userId
      ) {
        void fetchJsonAuthorized(
          endpointsCf.legalJobDelete(userId, task.id),
          "DELETE"
        ).catch((e) => console.error("[legal job delete]", e));
      }
      setTasks((prevTasks) => prevTasks.filter((t) => t.id !== task.id));
    },
    [userId, fetchJsonAuthorized]
  );

  const datesDifferenceInMilliseconds = (date1: Date, date2: Date) => {
    const diff = Math.abs(date1.getTime() - date2.getTime()); // Difference in milliseconds
    return diff;
  }

  const getTaskUpdate = async (task: Task) => {

    console.log(`📡 Requête get_processing_status pour ${task.file.filename}`);

    console.log("userId", userId);

    // Debug: payload exact envoyé à l'endpoint docprocessbar
    // (note: `useFetchWithMsal` ajoutera aussi `user_id` à partir de MSAL sur les POST JSON)
    console.log("POST docprocessbar payload (pre user_id injection)", {
      endpoint: endpointsCf.docprocessbar(userId),
      mode: task.mode,
      filename: task.file.filename,
      userId,
    });

    const response = await execute('POST', endpointsCf.docprocessbar(userId), {
      filename: task.file.filename,
      mode: task.mode
    });

    console.log(`📥 Réponse get_processing_status pour ${task.file.filename}:`, response);
  
    if (!response || typeof response !== 'object') {
      console.log(`⚠️ Réponse invalide pour ${task.file.filename}:`, response);
      return task;
    }

    if (response.status === "error") {
      return task;
    }

    const isTaskUpdated = () => {
      if(task.file.status !== response.status) {
        return true;
      } else if(task.file.nbProcessed !== response.nbProcessed) {
        return true;
      } else if(task.file.nbProcessedSummary !== response.nbProcessedSummary) {
        return true;
      } else {
        return false;
      }
    }
  
    var updatedTask = {
        id: task.id,
        type: task.type,
        timestamp: task.timestamp,
        createdAt: task.createdAt,
        updatedAt: isTaskUpdated() ? Date.now() : task.updatedAt,
        path: task.path,
        file: {
            filename: response.filename,
            nbPages: response.nbPages,
            nbProcessed: response.nbProcessed,
            nbProcessedSummary: response.nbProcessedSummary,
            status: response.status
        },
        status: task.status,
        mode: task.mode,
        page: task.page
    };

    //Si on vient de finir le processing, et que le statut est completed, on lance le résumé
    if((task.type === TaskType.UPLOAD && updatedTask.file.status === "completed") && task.status !== TaskStatus.SUMMARIZING) {
      getSummary(updatedTask.file.filename, updatedTask.file.nbPages, mode);
      updatedTask.status = TaskStatus.SUMMARIZING;
      notificationRef.current?.showNotification(task.file.filename, "Your document is ready for chat, now summarizing...");
      fileSelectorRef.current?.refreshFiles();
    }

    if(updatedTask.type === TaskType.SUMMARY && updatedTask.status !== TaskStatus.SUMMARIZING) {
      updatedTask.status = TaskStatus.SUMMARIZING;
      getSummary(updatedTask.file.filename, updatedTask.file.nbPages, mode);
      console.log(`🔄 Résumé lancé pour ${updatedTask.file.filename}`);
    }

    if(updatedTask.file.status === "summary_done" && task.status !== TaskStatus.COMPLETED) {
      updatedTask.status = TaskStatus.COMPLETED;
      notificationRef.current?.showNotification(task.file.filename, "The document summary is ready.");
      setPollingTasks(prevTasks => prevTasks.filter(t => t.id !== task.id));
    }

    if(updatedTask.status !== TaskStatus.COMPLETED && updatedTask.file.status !== TaskStatus.ERROR) {
      if(datesDifferenceInMilliseconds(new Date(), new Date(updatedTask.updatedAt)) > 1000 * 60 * 10) {
        updatedTask.status = TaskStatus.ERROR;
        setPollingTasks(prevTasks => prevTasks.filter(t => t.id !== task.id));
      }
    }



   /*const nbMinutesBeforeRelaunchSummary = 2;
   console.log(datesDifferenceInMilliseconds(new Date(updatedTask.updatedAt), new Date(updatedTask.createdAt)))
    if(task.status === TaskStatus.SUMMARIZING && datesDifferenceInMilliseconds(new Date(updatedTask.updatedAt), new Date(task.updatedAt)) > nbMinutesBeforeRelaunchSummary * 60 * 1000) {
      //On relance le résumé
      console.log("relance le résumé pour ", updatedTask.file.filename)
      //getSummary(updatedTask.file.filename, updatedTask.file.nbPages, mode);
      console.log(`🔄 Résumé relancé pour ${updatedTask.file.filename}`);
    }*/

    console.log("_________________updatedTask", updatedTask)
    updateTask(updatedTask);

  };

  const getSummary = useCallback(async (fileName: string, nbPages: number, mode: string) => {
    
    try {
      const response = await execute('POST', endpointsCf.hasSummary, {
        file_name: fileName,
        user_id: activeAccount?.username,
        mode: mode
      });

      const hasSummary = response?.hasSummary === true;
      const path = response?.path;

      console.log("hasSummary", hasSummary)
      console.log("path", path)

      if (!hasSummary) {
        try {
          console.log("nbPages", nbPages)
          await execute('POST', endpointsCf.summarize, {
            filename: fileName,
            userId: activeAccount?.username,
            mode: mode,
            path: path,
            nPages: nbPages,
          });
        } catch (error) {
          console.error(`❌ Erreur lors du résumé pour ${fileName}:`, error);
        }
      }
    } catch (error) {
      console.error(`❌ Erreur lors de la vérification du résumé pour ${fileName}:`, error);
    }
  
}, [activeAccount, execute, page]);


  useEffect(() => {
    let pollingInterval: NodeJS.Timeout;

    const checkProcessingStatus = async () => {
      tasks.filter(task => !pollingTasks.includes(task) && task.type !== TaskType.LEGAL_SEARCH && (task.status === TaskStatus.PROCESSING || task.status === TaskStatus.SUMMARIZING)).forEach(async (task) => {
        await getTaskUpdate(task);
        setPollingTasks(prevTasks => [...prevTasks, task]);
      });
    };

    pollingInterval = setInterval(checkProcessingStatus, 5000);

    return () => clearInterval(pollingInterval);
  }, [tasks, getTaskUpdate]);

  return (
    <TasksContext.Provider
      value={{
        tasks,
        setTasks,
        addTask,
        removeTask,
        selectFilebyName,
        updateTask
      }}
    >
      {children}
      <UploadNotification ref={notificationRef} />
    </TasksContext.Provider>
  );
};

export const useTasksContext = () => {
const context = useContext(TasksContext);
  if (context === undefined) {
    throw new Error("useTasksContext must be used within a TasksProvider");
  }
  return context;
};